// client/src/scenes/transactions/MyTransactions.jsx
import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  useTheme,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { gql, useQuery, useMutation } from "@apollo/client";
import TransactionForm from "./TransactionForm";
import TransactionBreakdownForm from "./TransactionBreakdownForm";
import { formatKes, getTransactionTypeMeta } from "./transactionTableUtils";

// Icons
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const GET_USER_TRANSACTIONS = gql`
  query {
    userAccountTransactions {
      id
      accountName
      amount
      transactionDateTime
      transactionReference
      confirmationMessage
      approved
      isProcessed
      transactionTypeId
    }
  }
`;

const CREATE_TRANSACTION_MUTATION = gql`
  mutation CreateAccountTransaction($input: CreateAccountTransactionInput!) {
    createAccountTransaction(input: $input) {
      isSuccess
      errorMessage
      data
    }
  }
`;

const UPDATE_BREAKDOWN_MUTATION = gql`
  mutation UpdateAccountTransactionBreakdown($input: TransactionBreakdownsInput!) {
    updateAccountTransactionBreakdown(input: $input) {
      isSuccess
      errorMessage
      data
    }
  }
`;

const GET_BREAKDOWNS_BY_TRANSACTION = gql`
  query AllocationQueueByTransaction($transactionId: Int!) {
    allocationQueueByTransaction(transactionId: $transactionId) {
      id
      accountTransactionId
      scheduleType
      scheduleTypeId
      scheduleId
      allocatedAmount
      status
      processingErrorMessage
      createdDate
      processedDate
    }
  }
`;

const ActionsMenu = ({ row, onView, onEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const locked = row.approved === true || row.isProcessed === true;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<VisibilityIcon fontSize="small" />}
        onClick={() => onView(row)}
        sx={{
          fontWeight: 700,
          textTransform: "none",
          whiteSpace: "nowrap",
          borderColor: alpha("#fff", 0.25),
        }}
      >
        View Breakdown
      </Button>

      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MenuItem
          disabled={locked}
          onClick={() => {
            setAnchorEl(null);
            onEdit(row);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Breakdown" />
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const MyTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data, loading, error, refetch } = useQuery(GET_USER_TRANSACTIONS);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownMode, setBreakdownMode] = useState("view");
  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState([]);

  const [createTransaction, { loading: mutationLoading }] = useMutation(
    CREATE_TRANSACTION_MUTATION,
    {
      onCompleted: (res) => {
        if (res.createAccountTransaction.isSuccess) {
          refetch();
          setIsFormOpen(false);
        } else {
          alert(
            res.createAccountTransaction.errorMessage ||
              "Failed to create transaction"
          );
        }
      },
      onError: (err) => {
        console.error("Mutation error:", err);
        alert(`An unexpected error occurred: ${err.message}`);
      },
    }
  );

  const [updateBreakdown, { loading: updatingBreakdown }] = useMutation(
    UPDATE_BREAKDOWN_MUTATION,
    {
      onCompleted: async (res) => {
        const r = res?.updateAccountTransactionBreakdown;
        if (r?.isSuccess) {
          await refetch();
          setBreakdownOpen(false);
          setSelectedTx(null);
          setSelectedBreakdowns([]);
          setBreakdownMode("view");
        } else {
          alert(r?.errorMessage || "Failed to update breakdowns");
        }
      },
      onError: (err) => {
        console.error("Update breakdown error:", err);
        alert(`An unexpected error occurred: ${err.message}`);
      },
    }
  );

  const { refetch: refetchBreakdowns } = useQuery(GET_BREAKDOWNS_BY_TRANSACTION, {
    variables: { transactionId: 0 },
    skip: true,
    fetchPolicy: "network-only",
  });

  const rows = useMemo(
    () =>
      (data?.userAccountTransactions || []).map((transaction, index) => ({
        ...transaction,
        sequence: index + 1,
      })),
    [data]
  );

  const handleOpenForm = () => setIsFormOpen(true);
  const handleCloseForm = () => setIsFormOpen(false);

  const handleSubmit = useCallback(
    (input) => {
      if (!input?.confirmationMessage) {
        alert("Transaction message cannot be empty.");
        return;
      }
      if (!Array.isArray(input?.breakdowns) || input.breakdowns.length === 0) {
        alert("Add at least one breakdown line item.");
        return;
      }
      createTransaction({ variables: { input } });
    },
    [createTransaction]
  );

  const openBreakdownModal = useCallback(async (tx, mode) => {
    try {
      setSelectedTx(tx);
      setBreakdownMode(mode);

      const res = await refetchBreakdowns({ transactionId: tx.id });
      const breakdownRows = res?.data?.allocationQueueByTransaction || [];

      setSelectedBreakdowns(
        breakdownRows.map((row) => ({
          scheduleType: row.scheduleType,
          scheduleTypeId: row.scheduleTypeId,
          scheduleId: row.scheduleId,
          allocatedAmount: row.allocatedAmount,
          status: row.status,
          processingErrorMessage: row.processingErrorMessage,
        }))
      );

      setBreakdownOpen(true);
    } catch (e) {
      console.error("Failed to load breakdowns:", e);
      alert("Failed to load breakdowns for this transaction.");
    }
  }, [refetchBreakdowns]);

  const submitEditBreakdown = (breakdowns) => {
    if (!selectedTx?.id) return;

    const payload = {
      accountTransactionId: selectedTx.id,
      breakdowns: breakdowns.map((b) => ({
        scheduleType: b.scheduleType,
        scheduleTypeId: Number(b.scheduleTypeId),
        scheduleId: Number(b.scheduleId),
        allocatedAmount: Number.parseFloat(b.allocatedAmount),
      })),
    };

    updateBreakdown({ variables: { input: payload } });
  };

  const columns = useMemo(() => [
    {
      field: "sequence",
      headerName: "#",
      flex: 0.25,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    { field: "accountName", headerName: "To", flex: 1, minWidth: 140 },
    {
      field: "transactionTypeId",
      headerName: "Type",
      flex: 0.7,
      minWidth: 140,
      renderCell: ({ value }) => {
        const typeMeta = getTransactionTypeMeta(value);
        const myTransactionTypeLabel =
          value === 1
            ? "To Group Account"
            : value === 2
              ? "From Group Account"
              : typeMeta.label;

        return (
          <Chip
            label={myTransactionTypeLabel}
            size="small"
            sx={{
              fontWeight: 700,
              ...typeMeta.sx,
            }}
          />
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.6,
      minWidth: 120,
      valueFormatter: ({ value }) => formatKes(value),
    },
    {
      field: "transactionDateTime",
      headerName: "Date",
      flex: 1,
      minWidth: 170,
      hideable: true,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      field: "approved",
      headerName: "Confirmed",
      flex: 0.6,
      minWidth: 120,
      renderCell: ({ value }) => (
        <Typography fontWeight={600} color={value ? "success.main" : "warning.main"}>
          {value ? "Confirmed" : "Pending"}
        </Typography>
      ),
    },
    {
      field: "transactionReference",
      headerName: "Reference",
      flex: 1.3,
      minWidth: 180,
      renderCell: ({ row, value }) => {
        const hasConfirmationMessage = Boolean(row.confirmationMessage?.trim());

        const referenceContent = (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value || "-"}
            </Typography>
            {hasConfirmationMessage ? (
              <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.secondary[200] }} />
            ) : null}
          </Stack>
        );

        if (!hasConfirmationMessage) {
          return referenceContent;
        }

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ p: 0.5, maxWidth: 360 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
                  Confirmation message
                </Typography>
                <Typography variant="body2">{row.confirmationMessage}</Typography>
              </Box>
            }
          >
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                cursor: "help",
              }}
            >
              {referenceContent}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 170,
      hideable: true,
      sortable: false,
      renderCell: ({ row }) => (
        <ActionsMenu
          row={row}
          onView={(tx) => openBreakdownModal(tx, "view")}
          onEdit={(tx) => openBreakdownModal(tx, "edit")}
        />
      ),
    },
  ], [theme, openBreakdownModal]);

  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box m={{ xs: "1rem", sm: "1.25rem 1.5rem", md: "1.5rem 2.5rem" }} sx={{ overflowX: "hidden" }}>
      <FlexBetween gap="0.75rem" flexWrap="wrap">
        <Header title="MY TRANSACTIONS" subtitle="Personal Account Activities" />
        <Button variant="contained" color="secondary" onClick={handleOpenForm}>
          Upload Transaction
        </Button>
      </FlexBetween>

      <Box
        mt={{ xs: "20px", md: "40px" }}
        height={{ xs: "calc(100vh - 260px)", md: "75vh" }}
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={loading || mutationLoading || updatingBreakdown}
          rows={rows}
          getRowId={(row) => row.id}
          columns={columns}
          disableRowSelectionOnClick
          columnVisibilityModel={{
            sequence: !isMobile,
            transactionReference: !isMobile,
          }}
          sx={{ minWidth: 0 }}
        />
      </Box>

      <TransactionForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        loading={mutationLoading}
      />

      <TransactionBreakdownForm
        open={breakdownOpen}
        mode={breakdownMode}
        initialBreakdowns={selectedBreakdowns}
        transaction={selectedTx}
        onClose={() => {
          setBreakdownOpen(false);
          setSelectedTx(null);
          setSelectedBreakdowns([]);
          setBreakdownMode("view");
        }}
        onSubmit={submitEditBreakdown}
        loading={updatingBreakdown}
      />
    </Box>
  );
};

export default MyTransactions;
